import type { StaffDetail } from '@/definitions/staff'
import { formatIsoDateNumeric } from '@/utils/dateUtils'

export interface StaffDetailField {
  label: string
  value: string
}

/**
 * A staff record's fields, in reading order, as label/value pairs.
 *
 * Built as data rather than as markup so the card can lay the same list out in
 * however many columns it has room for.
 *
 * The role is not here: the card's header already badges it, and repeating it
 * below would be the same fact twice. Values are otherwise shown as they are
 * stored, the id included, so they can be copied — only the birthdate is
 * reshaped, into a calendar date.
 *
 * A field with no value is kept rather than dropped, and rendered as an em dash,
 * so a gap in the record stays visible.
 */
export const buildStaffDetailFields = (detail: StaffDetail): StaffDetailField[] => [
  { label: 'First Name', value: detail.name_given },
  { label: 'Last Name', value: detail.name_family },
  { label: 'Nickname', value: detail.name_nickname ?? '' },
  { label: 'Email', value: detail.email },
  { label: 'Username', value: detail.username ?? '' },
  { label: 'Staff ID', value: detail.id },
  {
    label: 'Date of Birth',
    value: detail.birthdate ? formatIsoDateNumeric(detail.birthdate) : '',
  },
  { label: 'Prefixes', value: detail.prefixes ?? '' },
  { label: 'Suffixes', value: detail.suffixes ?? '' },
  { label: 'Credentials', value: detail.credentials ?? '' },
  { label: 'NPI', value: detail.npi ?? '' },
  { label: 'License Number', value: detail.license_number ?? '' },
]
